import React, { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "flowbite-react";
import { HiEye, HiCheck, HiX, HiTrash, HiArrowUp, HiArrowDown } from "react-icons/hi";
import { ChatContext, ChatContextInterface } from "../components/MSRContext";
import { BsClipboardCheckFill } from "react-icons/bs";
import { createRequestControlMessage } from "../utils/cmsgBuilder";
import { MyRequest } from "@/models/MyRequest";
import { useNavigate } from "react-router-dom";

const INVITE_MEMBER_EXPIRY_MS = 5 * 60 * 1000;

const isInviteMemberExpired = (request: MyRequest): boolean => {
    if (request.type !== 'invite-member' || request.status !== 'pending') return false;
    return Date.now() - new Date(request.createdTime).getTime() > INVITE_MEMBER_EXPIRY_MS;
};

const Requests: FC = function () {
    const { requestController, setreqCount, wrapper, handleremoveRequest } = useContext(ChatContext) as ChatContextInterface;
    const [requests, setRequests] = useState(requestController.Requests);
    const { t } = useTranslation("nav");

    const sortRequestsByTime = (requestsArray: MyRequest[]) => {
        return [...requestsArray].sort((a, b) => {
            const timeA = new Date(a.createdTime).getTime();
            const timeB = new Date(b.createdTime).getTime();
            return timeB - timeA; 
        });
    };

    useEffect(() => {
        const handleRequestsUpdate = () => {
            console.log("Requests updated, refreshing component");
            const sortedRequests = sortRequestsByTime(requestController.Requests);
            setRequests(sortedRequests);
            setreqCount(requestController.Requests.length);
        };

        window.addEventListener("requestsUpdate", handleRequestsUpdate);
        return () => {
            window.removeEventListener("requestsUpdate", handleRequestsUpdate);
        };
    }, [requestController, setreqCount]);

    useEffect(() => {
        const sortedRequests = sortRequestsByTime(requestController.Requests);
        setRequests(sortedRequests);
        setreqCount(requestController.Requests.length);
    }, [requestController]);

    const handleAccept = (request: MyRequest) => {
        requestController.acceptRequest(request);
        const sortedRequests = sortRequestsByTime(requestController.Requests);
        setRequests(sortedRequests);
    };

    const handleReject = (request: MyRequest) => {
        // remove this condition after scont support for rejecting invite-memeber request is added 
        if (request.type === "invite-member") {
            return;
        }
        requestController.rejectRequest(request);
        const sortedRequests = sortRequestsByTime(requestController.Requests);
        setRequests(sortedRequests);
    };

    const handleCancelRequest = (request: MyRequest) => {
        wrapper?.sendMsg(
            createRequestControlMessage(
                request.id,
                "cancel-ongoing",
                `job-to-cancel:${request.type}, response-required:YES`
            ),
            "application/rechat.cntl+json"
        );
        requestController.updateRequestStatusById(request.id, "cancelled");
        const updatedRequests = requests.map((req) =>
            req.id === request.id ? { ...req, status: "cancelled" } : req
        );
        const sortedRequests = sortRequestsByTime(updatedRequests);
        setRequests(sortedRequests);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-400';
            case 'accepted': return 'bg-green-400';
            case 'rejected': return 'bg-red-400';
            case 'cancelled': return 'bg-gray-400';
            case 'expired': return 'bg-orange-400';
            default: return 'bg-gray-400';
        }
    };

    const getStatusTextColor = (status: string) => {
        switch (status) {
            case 'pending': return 'text-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400';
            case 'accepted': return 'text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
            case 'rejected': return 'text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
            case 'cancelled': return 'text-gray-700 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400';
            case 'expired': return 'text-orange-700 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400';
            default: return 'text-gray-700 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400';
        }
    };

    const formatTimeAgo = (createdTime: string) => {
        const now = new Date().getTime();
        const created = new Date(createdTime).getTime();
        const diffMinutes = Math.floor((now - created) / (1000 * 60));
        
        if (diffMinutes < 1) return t('timeAgo.justNow');
        if (diffMinutes < 60) return t('timeAgo.minutes', { count: diffMinutes });
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) return t('timeAgo.hours', { count: diffHours });
        const diffDays = Math.floor(diffHours / 24);
        return t('timeAgo.days', { count: diffDays });
    };

    const pendingCount = requests.filter(req => req.status === 'pending' && !isInviteMemberExpired(req)).length;
    const navigate = useNavigate();

    return (
        <Dropdown
            arrowIcon={false}
            inline
            label={
                <span className="relative p-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200">
                    <span className="sr-only">{t('requestsTitle')}</span>
                    <BsClipboardCheckFill title={t('requestsTitle')}  className="text-xl text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors duration-200" />
                    {pendingCount > 0 && (
                        <span className="absolute flex items-center justify-center min-w-[18px] h-[18px] text-xs font-medium text-white rounded-full bg-red-500 -top-1 -right-1 animate-pulse">
                            {pendingCount}
                        </span>
                    )}
                </span>
            }
        >
            <div className="w-80 max-w-sm">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-50 rounded-t-lg dark:bg-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600">
                    <span className="flex items-center gap-2">
                        <BsClipboardCheckFill className="text-base" />
                        {t('requestsHeader')}
                    </span>
                    {requests.length > 0 && (
                        <span className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded-full">
                            {requests.length}
                        </span>
                    )}
                </div>

                {/* Requests List */}
                <div className="max-h-96 overflow-y-auto">
                    {requests.length > 0 ? (
                        requests.map((request, index) => (
                            <div
                                key={request.id}
                                className={`relative px-4 py-3 border-b border-gray-100 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200 ${
                                    index === requests.length - 1 ? 'border-b-0' : ''
                                }`}
                            >
                                {/* Request Header */}
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        {/* Avatar */}
                                        <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                            {(request.incoming ? request.sender.split("@")[0] : request.receiver[0].split("@")[0]).charAt(0).toUpperCase()}
                                        </div>
                                        
                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xs font-medium text-gray-900 dark:text-white truncate">
                                                    {request.incoming
                                                        ? request.sender.split("@")[0]
                                                        : Array.isArray(request.receiver)
                                                            ? (request.receiver[0]?.includes(":")
                                                                ? request.receiver[0].split(":")[1]
                                                                : request.receiver[0]?.split("@")[0])
                                                            : (request.receiver.includes(":")
                                                                ? request.receiver.split(":")[1]
                                                                : request.receiver.split("@")[0])
                                                    }                                               
                                                     </span>
                                                     
                                                <div className="flex items-center">
                                                    {request.incoming ? (
                                                        <HiArrowDown className="w-3 h-3 text-blue-500" />
                                                    ) : (
                                                        <HiArrowUp className="w-3 h-3 text-purple-500" />
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* Request Type */}
                                            <div className="flex items-center gap-2">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200">
                                                    {request.type}
                                                </span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {formatTimeAgo(request.createdTime)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Remove Button */}
                                    <button
                                        onClick={() => handleremoveRequest(request.id)}
                                        className="flex-shrink-0 ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors duration-200"
                                        title={t('removeRequest')}
                                    >
                                        <HiX className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Status */}
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        {(() => { const ds = isInviteMemberExpired(request) ? 'expired' : request.status; return (<>
                                        <div className={`w-2 h-2 rounded-full ${getStatusColor(ds)}`}></div>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusTextColor(ds)}`}>
                                            {ds === 'expired' ? 'Expired' : t(`status.${request.status}`)}
                                        </span>
                                        </>); })()}
                                    </div>
                                    
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                        request.incoming 
                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' 
                                            : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                                    }`}>
                                        {request.incoming ? t('incoming') : t('outgoing')}
                                    </span>
                                </div>

                                {/* Actions */}
                                {request.status === 'pending' && !isInviteMemberExpired(request) && (
                                    <div className="flex gap-1">
                                        {request.incoming ? (
                                            <>
                                                <button
                                                    onClick={() => handleAccept(request)}
                                                    className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                                                >
                                                    <HiCheck className="w-3 h-3" />
                                                    {t('action.accept')}
                                                </button>
                                                <button
                                                    disabled={request.type === "invite-member"}
                                                    onClick={() => handleReject(request)}
                                                    className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-600"
                                                >
                                                    <HiX className="w-3 h-3" />
                                                    {t('action.reject')}
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                onClick={() => handleCancelRequest(request)}
                                                className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                            >
                                                <HiTrash className="w-3 h-3" />
                                                {t('action.cancel')}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="px-4 py-8 text-center">
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                    <BsClipboardCheckFill className="w-6 h-6 text-gray-400" />
                                </div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('noRequests')}</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500">{t('caughtUp')}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer - View All Button */}
                {requests.length > 0 && (
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-b-lg border-t border-gray-200 dark:border-gray-600">
                        <button
                            onClick={() => {
                                navigate("/requestlist");
                            }}
                            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors duration-200"
                        >
                            <HiEye className="w-4 h-4" />
                            {t('viewAllRequests')}
                        </button>
                    </div>
                )}
            </div>
        </Dropdown>
    );
};

export default Requests;